import { from } from 'rxjs';
import { filter } from 'rxjs/operators';
import clearKnock from '@shoflo/studio-redux/actions/knock/clearKnock';
import createSources from '@shoflo/studio-redux/actions/source/createSources/createSources';
import cancelDisconnectSource from '@shoflo/studio-redux/actions/source/cancelDisconnectSource';
import { CAMERA_PRESENTER } from '@shoflo/polymer/constants/SourceItems';
import { SOURCE_ROLE_SPEAKER } from '@shoflo/polymer/constants/SourceRoleTypes';
import { UNASSIGNED } from '@studio/constants/Sources';

import { ACCEPTED, PRESENTER } from '@shoflo/polymer/constants/KnockTypes';
import { getBroadcastRouteMatch } from '@shoflo/studio-redux/utils/routeMatch';
import { selectLastPresenterSource } from '@shoflo/studio-redux/selectors/broadcastSourcesSelector';
import { BROADCAST_APPROVE_ALL_KNOCKS } from '@shoflo/studio-redux/actions/knock/types';
import { shouldHaveFeature } from '@studio/feature.flags/featureFlags';
import {
  getApiClient,
  createAttendees,
  sendMeetingToAttendees,
  handleFailedCreateAttendees,
  getPresenterSourcesToCreate
} from './utils';
import updateSource from 'actions/source/updateSource';
import { DEFAULT_ORDER_NUMBER } from 'utils/source';
import { VENDOR_AGORA } from '@studio/constants/Vendor';

/**
 * Emits an event to the accepted Presenter socket
 * BulkAcceptedKnock
 *
 * @param socket
 */
export function bulkAcceptedKnock(socket) {
  return store => next => async action => {
    const targetedActions = [`${BROADCAST_APPROVE_ALL_KNOCKS}_SUCCESS`];
    from([action])
      .pipe(filter($action => targetedActions.includes($action.type)))
      .subscribe(async $action => {
        const useAgora =
          shouldHaveFeature('studio.meetingVendor') === VENDOR_AGORA;

        // If using Agora, skip this middleware
        if (useAgora) return next(action);
        // Get all presenter requests and the accepted ones
        const approvedPresenterRequests = $action.payload.filter(
          request => request.type === PRESENTER && request.status === ACCEPTED
        );

        const [firstApprovedPresenterRequest] = approvedPresenterRequests;
        const broadcastId = firstApprovedPresenterRequest?.broadcastId;

        const requestPayload = approvedPresenterRequests.map(req => {
          return {
            userType: 'Presenter',
            presenterId: req.presenterId,
            sessionId: req.sessionId,
            presenterName: req.name
          };
        });

        const apiClient = getApiClient();
        let {
          meeting,
          attendeeRoster,
          program,
          team,
          attendeeCameraStatus,
          broadcastSettings,
          banners,
          streamEndpoints
        } = store.getState();

        const match = getBroadcastRouteMatch(store.getState);

        const { teamId, eventId } = match.params;

        // Check to see if the meeting is in session and that there are acceptedKnocks
        if (!meeting.meetingSession) return;

        // Clear the knock from state
        store.dispatch(clearKnock($action.payload));

        if (!requestPayload?.length) return;

        let createMeetingAttendeesResponse;

        try {
          createMeetingAttendeesResponse = await createAttendees(
            apiClient,
            teamId,
            eventId,
            broadcastId,
            meeting,
            requestPayload
          );
        } catch (e) {
          handleFailedCreateAttendees(
            e,
            store,
            approvedPresenterRequests,
            socket
          );
        }

        if (!createMeetingAttendeesResponse) return;

        sendMeetingToAttendees({
          store,
          socket,
          approvedPresenterRequests,
          createMeetingAttendeesResponse,
          attendeeCameraStatus,
          banners,
          streamEndpoints,
          meeting,
          program,
          attendeeRoster,
          broadcastSettings,
          broadcastId,
          team
        });

        const { order } = selectLastPresenterSource(store.getState());
        const { broadcastSources: { sources: existingSources = [] } = {} } =
          store.getState();

        // Separate knocks into those that match existing disconnected/unassigned
        // sources and those that need new sources
        const knocksNeedingNewSources = [];
        const matchedSourceIds = new Set();

        approvedPresenterRequests.forEach((knock, i) => {
          const createdAttendee = createMeetingAttendeesResponse[i];
          const attendeeId =
            createdAttendee.attendeeResponse.Attendee.AttendeeId;
          const knockName = knock.name?.toLowerCase().replaceAll(/\s+/g, '');
          const matchingSource = existingSources.find(
            source =>
              source.type === CAMERA_PRESENTER &&
              !matchedSourceIds.has(source._id) &&
              source.title?.toLowerCase().replaceAll(/\s+/g, '') ===
                knockName &&
              (source.disconnected || source.value === UNASSIGNED)
          );

          if (matchingSource) {
            matchedSourceIds.add(matchingSource._id);

            // Cancel pending disconnect timeout
            store.dispatch(cancelDisconnectSource(matchingSource._id));

            // Only send fields the API supports
            const updateFields = {
              value: attendeeId,
              title: knock.name,
              description: knock.description,
              cameraUnavailable: knock.cameraUnavailable || false
            };

            if (knock?.userId) {
              updateFields.userId = knock.userId;
              updateFields.role = SOURCE_ROLE_SPEAKER;
            }

            store.dispatch(updateSource(matchingSource._id, updateFields));
          } else {
            knocksNeedingNewSources.push({ knock, createdAttendee });
          }
        });

        if (knocksNeedingNewSources.length > 0) {
          const presentersSources = getPresenterSourcesToCreate({
            teamId,
            eventId,
            broadcastId,
            approvedPresenterRequests: knocksNeedingNewSources.map(
              k => k.knock
            ),
            createMeetingAttendeesResponse: knocksNeedingNewSources.map(
              k => k.createdAttendee
            ),
            lastPresenterOrder: order || DEFAULT_ORDER_NUMBER
          });

          store.dispatch(createSources(presentersSources));
        }
      });
    return next(action);
  };
}

export default bulkAcceptedKnock;
